import { useEffect, useRef, useState } from "react"
import { ArrowRight } from "lucide-react"
import { cn } from "@strivelabs/ui-core"

export type ChoiceOption = {
  id: string
  name: string
  description?: string
}

type ChoiceOptionsProps = {
  options: ChoiceOption[]
  value: string
  onChange: (id: string) => void
  onSelect?: (id: string) => void
  autoFocus?: boolean
}

export function ChoiceOptions({
  options,
  value,
  onChange,
  onSelect,
  autoFocus = true,
}: ChoiceOptionsProps) {
  const [focusedIndex, setFocusedIndex] = useState(() =>
    options.findIndex((o) => o.id === value),
  )
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setFocusedIndex(options.findIndex((o) => o.id === value))
  }, [value, options])

  useEffect(() => {
    if (autoFocus) listRef.current?.focus()
  }, [autoFocus])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault()
      setFocusedIndex((i) => (i < 0 ? 0 : (i + 1) % options.length))
      return
    }
    if (e.key === "ArrowUp") {
      e.preventDefault()
      setFocusedIndex((i) =>
        i < 0 ? options.length - 1 : (i - 1 + options.length) % options.length,
      )
      return
    }
    if (e.key === "Enter") {
      e.preventDefault()
      const opt = options[focusedIndex]
      if (!opt) return
      onChange(opt.id)
      onSelect?.(opt.id)
      return
    }
    const digit = Number(e.key)
    if (!Number.isNaN(digit) && digit >= 1 && digit <= options.length) {
      e.preventDefault()
      const opt = options[digit - 1]
      if (!opt) return
      setFocusedIndex(digit - 1)
      onChange(opt.id)
      onSelect?.(opt.id)
    }
  }

  return (
    <div
      ref={listRef}
      role="listbox"
      tabIndex={0}
      aria-activedescendant={options[focusedIndex]?.id}
      onKeyDown={handleKeyDown}
      className="focus:outline-none"
    >
      <div className="flex flex-col gap-2">
        {options.map((opt, idx) => {
          const isSelected = value === opt.id
          const isFocused = focusedIndex === idx
          return (
            <button
              key={opt.id}
              id={opt.id}
              role="option"
              aria-selected={isSelected}
              type="button"
              onMouseEnter={() => setFocusedIndex(idx)}
              onClick={() => {
                onChange(opt.id)
                onSelect?.(opt.id)
              }}
              className={cn(
                "group relative flex w-full items-center gap-3 rounded-lg border bg-background px-4 py-3 text-left transition-all",
                isSelected
                  ? "border-primary bg-primary/5 shadow-sm"
                  : "border-border hover:border-primary/40 hover:bg-primary/[0.03]",
                !isSelected && isFocused && "border-primary/50 bg-primary/[0.04]",
              )}
            >
              <span
                className={cn(
                  "flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-semibold transition-colors",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary",
                )}
              >
                {idx + 1}
              </span>
              <div className="flex min-w-0 flex-1 flex-col">
                <span
                  className={cn(
                    "text-sm font-medium",
                    isSelected ? "text-foreground" : "text-foreground/90",
                  )}
                >
                  {opt.name}
                </span>
                {opt.description && (
                  <span className="mt-0.5 text-xs text-foreground/60">
                    {opt.description}
                  </span>
                )}
              </div>
              <ArrowRight
                className={cn(
                  "h-4 w-4 shrink-0 transition-all",
                  isSelected
                    ? "text-primary opacity-100"
                    : isFocused
                      ? "text-primary/70 opacity-100"
                      : "text-muted-foreground opacity-0",
                )}
              />
            </button>
          )
        })}
      </div>
    </div>
  )
}
